/**
 * Workflows — All workflows (simple + complex) with AI Supervisor indicators
 *
 * Default tab for the Automate module. Shows every workflow as a card —
 * from simple 2-node automations (cron → agent) to complex multi-agent pipelines.
 * Each card shows purpose preview, supervisor mode, complexity, trigger type.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Workflow, Play, Clock, Search,
  Zap, Shield, Brain, Globe, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { AgentIcon } from '@/components/shared/icon-picker';
import { cn } from '@/lib/utils';
import {
  WORKFLOW_STATUS_STYLES,
  type WorkflowStatus,
  type WorkflowComplexity,
  type WorkflowTriggerType,
} from '@/lib/mock-automate-data';
import { useWorkflows } from '@/lib/use-automate';

export const Route = createFileRoute('/automate/')({
  component: WorkflowsPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms === 0) return '--';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const TRIGGER_ICONS: Record<WorkflowTriggerType, { icon: typeof Clock; label: string }> = {
  schedule: { icon: Clock, label: 'Scheduled' },
  event: { icon: Zap, label: 'Event' },
  webhook: { icon: Globe, label: 'Webhook' },
  manual: { icon: Play, label: 'Manual' },
};

const COMPLEXITY_STYLES: Record<WorkflowComplexity, { label: string; color: string }> = {
  simple: { label: 'Simple', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20' },
  standard: { label: 'Standard', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  complex: { label: 'Complex', color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
};

const SUPERVISOR_STYLES: Record<string, { label: string; color: string }> = {
  minimal: { label: 'Minimal', color: 'text-zinc-700 dark:text-zinc-300' },
  standard: { label: 'Standard', color: 'text-blue-700 dark:text-blue-300' },
  thorough: { label: 'Thorough', color: 'text-violet-700 dark:text-violet-300' },
};

// ── Page ────────────────────────────────────────────────────────────────────

function WorkflowsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | WorkflowStatus>('all');
  const [complexityFilter, setComplexityFilter] = useState<'all' | WorkflowComplexity>('all');
  const [triggerFilter, setTriggerFilter] = useState<'all' | WorkflowTriggerType>('all');

  // Real-data: automations + workflow_definitions joined via /automate-workflows
  const { data: workflows = [] } = useWorkflows();

  const filtered = useMemo(() => {
    return workflows.filter(wf => {
      if (statusFilter !== 'all' && wf.status !== statusFilter) return false;
      if (complexityFilter !== 'all' && wf.complexity !== complexityFilter) return false;
      if (triggerFilter !== 'all' && wf.triggerType !== triggerFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return wf.name.toLowerCase().includes(q) || wf.description.toLowerCase().includes(q) || wf.purpose.toLowerCase().includes(q);
    });
  }, [workflows, search, statusFilter, complexityFilter, triggerFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');

  const total = workflows.length;
  const active = workflows.filter(w => w.status === 'active').length;
  const simple = workflows.filter(w => w.complexity === 'simple').length;
  const complex = workflows.filter(w => w.complexity !== 'simple').length;

  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{total}</p><p className="text-xs text-muted-foreground">Total Workflows</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{active}</p><p className="text-xs text-muted-foreground">Active</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{simple}</p><p className="text-xs text-muted-foreground">Simple (Agent)</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{complex}</p><p className="text-xs text-muted-foreground">Multi-Step</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name, description, or purpose..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger aria-label="Filter workflows by status" className="w-28 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={complexityFilter} onValueChange={v => setComplexityFilter(v as typeof complexityFilter)}>
          <SelectTrigger aria-label="Filter workflows by complexity" className="w-32 h-9 text-xs"><SelectValue placeholder="Complexity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="simple">Simple</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="complex">Complex</SelectItem>
          </SelectContent>
        </Select>
        <Select value={triggerFilter} onValueChange={v => setTriggerFilter(v as typeof triggerFilter)}>
          <SelectTrigger aria-label="Filter workflows by trigger type" className="w-32 h-9 text-xs"><SelectValue placeholder="Trigger" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Triggers</SelectItem>
            <SelectItem value="schedule">Scheduled</SelectItem>
            <SelectItem value="event">Event</SelectItem>
            <SelectItem value="webhook">Webhook</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} workflow{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Workflow Table */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[minmax(300px,2fr)_90px_90px_90px_70px_100px_80px_100px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
              <SortableHeader label="Workflow" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Type" sortKey="complexity" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Trigger" sortKey="triggerType" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Nodes" sortKey="nodeCount" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Runs" sortKey="runCount" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Success" sortKey="successRate" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Supervisor" sortKey="supervisorMode" currentSort={sortConfig} onSort={handleSort} />
            </div>
            <div className="divide-y">
              {sorted.map(wf => {
                const ss = WORKFLOW_STATUS_STYLES[wf.status];
                const cs = COMPLEXITY_STYLES[wf.complexity];
                const triggerInfo = TRIGGER_ICONS[wf.triggerType];
                const TriggerIcon = triggerInfo.icon;
                const supervisorStyle = SUPERVISOR_STYLES[wf.supervisorMode];

                return (
                  <div
                    key={wf.id}
                    className={cn('grid grid-cols-[minmax(300px,2fr)_90px_90px_90px_70px_100px_80px_100px] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition-colors cursor-pointer group', wf.status === 'paused' && 'opacity-75')}
                    onClick={() => navigate({ to: '/automate/workflows/$id', params: { id: wf.id } } as any)}
                  >
                    {/* Name + description + purpose */}
                    <div className="flex items-center gap-3 min-w-0">
                      <AgentIcon iconName={wf.agentIcon ?? 'bot'} colorName={wf.agentIconColor ?? 'violet'} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{wf.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{wf.description}</p>
                        <p className="text-[11px] text-muted-foreground italic truncate mt-0.5">"{wf.purpose}"</p>
                        {wf.successCriteria.length > 0 && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Shield className="h-3 w-3 text-primary" />
                            <span className="text-[10px] text-muted-foreground">{wf.successCriteria.length} criteria · {wf.constraints.length} constraints</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', ss.color)}>{ss.label}</Badge>

                    {/* Complexity */}
                    <Badge variant="outline" className={cn('text-[10px] w-fit', cs.color)}>{cs.label}</Badge>

                    {/* Trigger */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title={triggerInfo.label}>
                      <TriggerIcon className="h-3.5 w-3.5" />
                      <span className="truncate">{triggerInfo.label}</span>
                    </div>

                    {/* Nodes */}
                    <span className="text-sm text-muted-foreground">{wf.nodeCount}</span>

                    {/* Runs + duration */}
                    <div className="text-xs text-muted-foreground">
                      <span>{wf.runCount} runs</span>
                      {wf.avgDurationMs > 0 && <p className="text-[11px] text-muted-foreground">{formatDuration(wf.avgDurationMs)} avg</p>}
                    </div>

                    {/* Success rate — backend stores 0..100; clamp + display as % (#100) */}
                    {wf.successRate > 0 ? (() => {
                      const clamped = Math.min(100, Math.max(0, wf.successRate));
                      return (
                        <span className={cn('text-sm font-semibold', clamped >= 90 ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300')}>
                          {Math.round(clamped)}%
                        </span>
                      );
                    })() : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}

                    {/* Supervisor */}
                    <div className="flex items-center gap-1.5">
                      <Brain className={cn('h-3.5 w-3.5', supervisorStyle.color)} />
                      <span className={cn('text-xs', supervisorStyle.color)}>{supervisorStyle.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Workflow className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">No workflows found</p>
          <p className="text-xs text-muted-foreground mt-1">
            {/* BUG-671 — empty-state copy now reflects the active filters.
                The stale "v1.2 / v1.1" line was caught by Phase 2 QA
                (AUTO-LIST-007) — workflow creation has shipped, and the
                trigger / complexity / status filters all need their own
                no-match message. */}
            {search || statusFilter !== 'all' || complexityFilter !== 'all' || triggerFilter !== 'all'
              ? 'No workflows match the current filters. Try clearing one of the filters above.'
              : 'No workflows yet. Create one from the "New Workflow" button or the playbooks catalog.'}
          </p>
          <Button
            size="sm"
            className="mt-4 gap-1.5"
            onClick={() => navigate({ to: '/automate', search: { action: 'create' } } as never)}
          >
            <Plus className="h-3.5 w-3.5" /> New Workflow
          </Button>
        </div>
      )}
    </div>
  );
}
